<template>
  <b-row
    class="files-row"
    aria-busy="true"
    aria-label="Loading files"
    data-test="page-loading"
  >
    <b-col cols="12" lg="3" class="pr-lg-4 mb-3 mb-lg-0">
      <!-- Below lg the real page shows a single button for the sidebar. -->
      <div class="sk-switcher d-lg-none">
        <span class="sk-switcher-text">
          <span class="sk-label">All files</span>
          <span class="sk-bar sk-hint"></span>
        </span>
        <span class="sk-bar sk-count"></span>
      </div>

      <b-card class="sk-card d-none d-lg-block" body-class="p-2">
        <div v-for="label in sidebarLabels" :key="label" class="sk-item">
          <span class="sk-label">{{ label }}</span>
          <span class="sk-bar sk-count"></span>
        </div>

        <hr class="sk-divider" />

        <div class="sk-heading">Folders</div>
        <div v-for="n in 3" :key="`folder-${n}`" class="sk-item sk-folder">
          <span class="sk-bar sk-folder-name"></span>
          <span class="sk-bar sk-count"></span>
        </div>
      </b-card>
    </b-col>

    <b-col cols="12" lg="9">
      <b-card class="sk-card" body-class="p-3 p-md-4">
        <h4 class="sk-title">
          All files
          <span class="sk-bar sk-title-count"></span>
        </h4>

        <div class="sk-toolbar">
          <div class="sk-search">Search file name...</div>
          <span v-if="canUpload" class="sk-bar sk-upload"></span>
        </div>

        <div class="sk-chips">
          <span v-for="n in 5" :key="`chip-${n}`" class="sk-bar sk-chip"></span>
        </div>

        <div class="sk-table">
          <div class="sk-head">
            <span class="sk-th sk-check"></span>
            <span class="sk-th sk-name">Name</span>
            <span class="sk-th sk-owner d-none d-md-block">Owner</span>
            <span class="sk-th sk-added d-none d-sm-block">Added</span>
          </div>
          <div v-for="n in 8" :key="`row-${n}`" class="sk-row">
            <span class="sk-check"><span class="sk-box"></span></span>
            <span class="sk-name">
              <span class="sk-bar sk-icon"></span>
              <span class="sk-bar sk-file-name"></span>
            </span>
            <span class="sk-owner d-none d-md-block"
              ><span class="sk-bar sk-owner-name"></span
            ></span>
            <span class="sk-added d-none d-sm-block"
              ><span class="sk-bar sk-added-text"></span
            ></span>
          </div>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
// The page's own shape while its first load is under way: real labels, grey
// placeholders where the data goes, so nothing jumps when it arrives.
export default {
  props: {
    canUpload: { type: Boolean, default: true },
  },

  data() {
    return {
      sidebarLabels: ['All files', 'Recent', 'Expiring', 'Received', 'Sent'],
    }
  },
}
</script>

<style scoped>
/* This site's columns have no side padding, so the row's -15px pull-out would
   push the cards to (or past) the screen edge; below lg the container's own
   padding is the only margin. */
@media (max-width: 991.98px) {
  .files-row {
    margin-right: 0;
    margin-left: 0;
  }
}

/* The single button the real page shows for the sidebar below lg. */
.sk-switcher {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 56px;
  padding: 8px 16px;
  background-color: #ffffff;
  border: 1px solid #e1e5ea;
  border-radius: 14px;
}

.sk-switcher-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sk-hint {
  width: 110px;
  height: 10px;
}

.sk-card {
  background-color: #ffffff;
  border: 1px solid #e1e5ea;
  border-radius: 14px;
}

/* The grey placeholder bars all share one soft pulse. */
.sk-bar,
.sk-box {
  display: inline-block;
  background-color: #eceff2;
  border-radius: 6px;
  animation: sk-pulse 1.4s ease-in-out infinite;
}

@keyframes sk-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sk-bar,
  .sk-box {
    animation: none;
  }
}

/* Sidebar */
.sk-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin: 2px 0;
  min-height: 43px;
}

.sk-label {
  color: #111827;
  font-size: 14px;
  font-weight: 400;
}

.sk-count {
  width: 22px;
  height: 14px;
}

.sk-divider {
  border: 0;
  border-top: 1px solid #e1e5ea;
  margin: 12px 2px;
}

.sk-heading {
  padding: 4px 12px;
  color: #111827;
  font-size: 14px;
  font-weight: 700;
}

.sk-folder-name {
  width: 55%;
  height: 14px;
}

/* Content */
.sk-title {
  margin-bottom: 24px;
  color: #111827;
  font-weight: 700;
}

.sk-title-count {
  width: 54px;
  height: 14px;
  margin-left: 8px;
  vertical-align: middle;
}

.sk-toolbar {
  display: flex;
  gap: 12px;
}

.sk-search {
  flex: 1;
  padding: 12px 14px;
  border: 2px solid #e1e5ea;
  border-radius: 6px;
  color: #9ca3af;
  font-size: 14px;
  line-height: 24px;
}

.sk-upload {
  width: 148px;
  height: 52px;
  border-radius: 6px;
}

.sk-chips {
  display: flex;
  gap: 8px;
  margin: 24px 0;
  overflow: hidden;
}

.sk-chip {
  flex-shrink: 0;
}

@media (max-width: 575.98px) {
  .sk-upload {
    width: 50px;
  }

  .sk-title {
    margin-bottom: 16px;
  }

  .sk-chips {
    margin: 16px 0;
  }
}

.sk-chip {
  width: 84px;
  height: 32px;
  border-radius: 999px;
}

/* Table */
.sk-table {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.sk-head,
.sk-row {
  display: flex;
  align-items: center;
  padding: 0 14px;
}

.sk-head {
  min-height: 44px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.sk-th {
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
}

.sk-row {
  min-height: 49px;
  border-bottom: 1px solid #e5e7eb;
}

.sk-row:last-child {
  border-bottom: 0;
}

.sk-check {
  flex: 0 0 40px;
}

.sk-box {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.sk-name {
  display: flex;
  flex: 1 1 50%;
  align-items: center;
  gap: 12px;
}

.sk-owner {
  flex: 0 0 22%;
}

.sk-added {
  flex: 0 0 18%;
}

.sk-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.sk-file-name {
  width: 40%;
  height: 14px;
}

.sk-owner-name {
  width: 56px;
  height: 14px;
}

.sk-added-text {
  width: 76px;
  height: 14px;
}
</style>
